/* eslint-disable  */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BgpNeighbor, bgpNeighborsColumns as defaultBgpNeighborsColumns } from './BgpDetailsData';
import GenericCard from '@ui-components/Card/GenericCard';
import GenericButton from '@ui-components/Button/GenericButton';
import GenericInput from '@ui-components/Input/GenericInput';
import { GenericTable, GenericTableColumn } from '@ui-components/Table/GenericTable';
import BgpModal from './BgpModal/BgpModal';
import { ReactComponent as IconEdit } from '../../../../shared/assets/Icons/Icon_Edit.svg';
import { ReactComponent as IconDelete } from '../../../../shared/assets/Icons/Icon_Delete.svg';
import { getReflectorBgpNeighbors, getClusterBgpNeighbors, updateReflectorBgpConfig, deleteReflectorBgpNeighbor, ReflectorBgpConfig, getReflectorBgpConfig } from '../request/request';
import GenericAlert from '@ui-components/Alert/GenericAlert';
import { getBgpConfig, updateBgpConfig } from '../Cluster/request/request';
import { AxiosError } from 'axios';
import EmptyItems from '@shared/components/EmptyItems';
import { useAppSelector } from '@redux/hooks';
import { GenericTag } from '@ui-components/Tag/GenericTag';
import GenericTooltip from '@ui-components/Tooltip/GenericTooltip';
import { hasStatus, buildStatusLabel, getStatusVariant, buildBfdLabel, getBfdVariant, buildReflectorDownLabel } from '../../../helpers/bgpValidation';
import { formatCiscoUptime } from '../../../helpers/formattersShapedData';
import { DataStateView } from 'kontrolEdge/components/Backbone/BackboneDetails/StateViews';
import { TableSkeleton, SkeletonLine } from '@ui-components/Skeleton/GenericSkeleton';
import { describeCapSource, resolveInstanceCaps } from '../../../helpers/instanceLimits';
import {
  getTableBodyMaxHeight,
  getInitialRowsPerPage,
  REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS,
} from '../../../helpers/tableViewport';

/** SideNav + detail tabs + BGP header/Add row + card padding (CTX-331). */
const BGP_NEIGHBORS_CHROME_PX = 400;


interface BgpDetailsPageProps {
  config: any;
  reflectorId?: string;
  clusterId?: string;
  getAccessTokenSilently: () => Promise<string>;
  showReflector2?: boolean;
  routerId2?: string;
  onRouterId2Change?: (val: string) => void;
  context?: 'reflector' | 'cluster';
  parentLoading?: boolean; // Indicates if parent is currently fetching config
  reflectorName?: string; // Reflector name from overview API call
}


// Helper to build ctxrConfigArray for cluster
function buildCtxrConfigArray(reflectors: number[], interfaceIds: number[]) {
  if (!Array.isArray(reflectors) || !Array.isArray(interfaceIds) || reflectors.length !== interfaceIds.length) return [];
  return reflectors.map((reflectorId, idx) => ({
    reflectorInstanceId: reflectorId,
    interfaceAddressId: interfaceIds[idx]
  }));
}

const BgpDetailsPage: React.FC<BgpDetailsPageProps> = ({
  config,
  reflectorId,
  clusterId,
  getAccessTokenSilently,
  showReflector2 = false,
  routerId2 = '',
  onRouterId2Change,
  context = 'reflector',
  parentLoading = false,
  reflectorName: propReflectorName,
}) => {
  const navigate = useNavigate();
  const { tenantId } = useAppSelector((state: any) => state.selection);
  const [localAs, setLocalAs] = useState(config?.localAs || '');
  const [routerId, setRouterId] = useState(config?.routerId || '');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editRow, setEditRow] = useState<any>(null);
  const [neighbors, setNeighbors] = useState<BgpNeighbor[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasNoInitialConfig, setHasNoInitialConfig] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [localAsError, setLocalAsError] = useState('');
  const [routerIdError, setRouterIdError] = useState('');
  const [deleteConfirmState, setDeleteConfirmState] = useState<{
    isOpen: boolean;
    neighborId: number | null;
    neighborAddress: string;
  }>({
    isOpen: false,
    neighborId: null,
    neighborAddress: '',
  });
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    variant: "success" | "error";
  }>({
    isOpen: false,
    title: "",
    description: "",
    variant: "success"
  });

  /** Fields from GET .../bgp-config when this page fetches internally (parent may not have merged yet). */
  const [fetchedBgpConfigBody, setFetchedBgpConfigBody] = useState<Record<string, unknown> | null>(null);

  const clusterReflectors = config?.clusterReflectors || [];
  const clusterReflectorNames = config?.clusterReflectorNames || [];
  const clusterInterfaceIds = config?.clusterInterfaceIds || [];
  const clusterReflectorRoles = config?.clusterReflectorRoles || []; // Array of { instanceId, role }

  /** From GET bgp-config: `reflectorInstance.instanceLimits` (parent `config` or in-page fetch). */
  const storedInstanceLimits =
    config?.reflectorInstance?.instanceLimits ??
    (fetchedBgpConfigBody as { reflectorInstance?: { instanceLimits?: unknown } } | null)
      ?.reflectorInstance?.instanceLimits ??
    null;

  const instanceCaps = resolveInstanceCaps(storedInstanceLimits);
  const bgpNeighborCap = instanceCaps.bgpSessions;
  const isAtBgpNeighborLimit = neighbors.length >= bgpNeighborCap;

  const addBgpNeighborDisabledReason = isAtBgpNeighborLimit
    ? `BGP neighbor limit reached (${neighbors.length}/${bgpNeighborCap} ${describeCapSource(
        instanceCaps.source
      )}).`
    : undefined;

  const reflectorName = context === 'reflector' 
    ? ((propReflectorName && propReflectorName.trim()) || (config?.reflectorInstance?.name && config.reflectorInstance.name.trim()) || (reflectorId ? `Reflector-${reflectorId}` : 'Reflector'))
    : (reflectorId ? `Reflector-${reflectorId}` : 'Reflector');
  const ctxrConfigArray = context === 'cluster' ? buildCtxrConfigArray(clusterReflectors, clusterInterfaceIds) : undefined;

  // Validation functions
  const validateNumberField = (value: string): string => {
    if (!value) return ""; // Empty is valid (will be validated by required check)
    const validPattern = /^[0-9]*$/;
    if (!validPattern.test(value)) {
      return "Invalid format";
    }
    return "";
  };

  const validateIPAddress = (value: string): string => {
    if (!value) return ""; // Empty is valid (will be validated by required check)
    // Allow numbers and dots only
    const validPattern = /^[0-9.]*$/;
    if (!validPattern.test(value)) {
      return "Invalid format";
    }
    // Basic IP format validation
    const ipParts = value.split('.');
    if (ipParts.length > 4) {
      return "Invalid format";
    }
    // Validate each completed segment
    const isCompleteSegment = value.endsWith('.') || ipParts.length === 4;
    for (let i = 0; i < ipParts.length; i++) {
      const part = ipParts[i];
      if (part && (i < ipParts.length - 1 || isCompleteSegment)) {
        const numValue = parseInt(part);
        if (isNaN(numValue) || numValue > 255 || numValue < 0) {
          return "Invalid format";
        }
      }
    }
    return "";
  };

  // Handlers for input fields with validation
  const handleLocalAsChange = (value: string) => {
    const filteredValue = value.replace(/[^0-9]/g, '');
    setLocalAs(filteredValue);
    setLocalAsError(validateNumberField(filteredValue));
    userEditedRef.current.localAs = true; // Mark as user-edited
  };

  const handleRouterIdChange = (value: string) => {
    const filteredValue = value.replace(/[^0-9.]/g, '');
    setRouterId(filteredValue);
    setRouterIdError(validateIPAddress(filteredValue));
    userEditedRef.current.routerId = true; // Mark as user-edited
  };
  
  // Handler for routerId2 changes
  const handleRouterId2Change = (value: string) => {
    if (onRouterId2Change) {
      onRouterId2Change(value);
      userEditedRef.current.routerId2 = true; // Mark as user-edited
    }
  };

  // Use ref to track if we've already fetched to prevent duplicate requests
  const hasFetchedRef = useRef(false);
  const fetchKeyRef = useRef<string>('');
  const getAccessTokenSilentlyRef = useRef(getAccessTokenSilently);

  // First load = spinner overlay only; secondary/refresh = skeletons
  const hasStartedLoadRef = useRef(false);
  const hasCompletedFirstLoadRef = useRef(false);
  useEffect(() => {
    if (isLoadingConfig || tableLoading) hasStartedLoadRef.current = true;
    if (!isLoadingConfig && !tableLoading && hasStartedLoadRef.current) {
      hasCompletedFirstLoadRef.current = true;
    }
  }, [isLoadingConfig, tableLoading]);
  const isFirstLoad = (isLoadingConfig || tableLoading) && !hasCompletedFirstLoadRef.current;
  
  // Track the last config values we synced from to prevent resetting user input
  const lastSyncedConfigRef = useRef<{
    localAs?: string | number | null;
    routerId?: string | null;
    routerId2?: string | null;
  } | null>(null);
  
  // Track if user has manually edited fields (to prevent resetting from config)
  const userEditedRef = useRef<{
    localAs: boolean;
    routerId: boolean;
    routerId2: boolean;
  }>({
    localAs: false,
    routerId: false,
    routerId2: false,
  });

  // Update ref when getAccessTokenSilently changes
  useEffect(() => {
    getAccessTokenSilentlyRef.current = getAccessTokenSilently;
  }, [getAccessTokenSilently]);

  // Use config prop if available, otherwise fetch
  useEffect(() => {
    // If parent is still loading, wait for it to finish
    if (parentLoading) {
      setIsLoadingConfig(true);
      return; // Don't fetch yet, wait for parent
    }

    // Create a stable key based on the fetch parameters
    const fetchKey = `${context}-${clusterId || ''}-${reflectorId || ''}`;
    
    // Reset fetch flag and user-edited flags if the key changed (switched to different cluster/reflector)
    if (fetchKeyRef.current !== fetchKey) {
      hasFetchedRef.current = false;
      fetchKeyRef.current = fetchKey;
      lastSyncedConfigRef.current = null;
      setFetchedBgpConfigBody(null);
      // Reset user-edited flags when switching clusters/reflectors
      userEditedRef.current = {
        localAs: false,
        routerId: false,
        routerId2: false,
      };
    }

    // If config prop is provided, use it instead of fetching
    // Parent has already fetched, so trust its result
    if (config !== undefined) {
      // Config prop was explicitly passed (even if null/empty)
      // Check if config has actual values (not just empty strings)
      const hasLocalAs = config && config.localAs !== undefined && config.localAs !== null && config.localAs !== '';
      const hasRouterId = config && config.routerId !== undefined && config.routerId !== null && config.routerId !== '';
      
      // Check if config values actually changed (not just reference)
      const currentConfigValues = {
        localAs: config?.localAs,
        routerId: config?.routerId,
        routerId2: config?.routerId2,
      };
      const lastSynced = lastSyncedConfigRef.current;
      const configValuesChanged = !lastSynced || 
        lastSynced.localAs !== currentConfigValues.localAs ||
        lastSynced.routerId !== currentConfigValues.routerId ||
        lastSynced.routerId2 !== currentConfigValues.routerId2;
      
      if (config && (hasLocalAs || hasRouterId)) {
        // Parent provided config with data
        // Only sync fields that user hasn't edited and that actually changed
        if (configValuesChanged) {
          const localAsValue = config.localAs?.toString() || '';
          const routerIdValue = config.routerId || '';
          const routerId2Value = config.routerId2 || '';
          
          // Only update localAs if user hasn't edited it
          if (!userEditedRef.current.localAs) {
            setLocalAs(localAsValue);
            setLocalAsError(validateNumberField(localAsValue));
          }
          
          // Only update routerId if user hasn't edited it
          if (!userEditedRef.current.routerId) {
            setRouterId(routerIdValue);
            setRouterIdError(validateIPAddress(routerIdValue));
          }
          
          // Only update routerId2 if user hasn't edited it
          if (!userEditedRef.current.routerId2 && onRouterId2Change) {
            onRouterId2Change(routerId2Value);
          }
          
          // Update last synced config
          lastSyncedConfigRef.current = currentConfigValues;
        }
        
        setHasNoInitialConfig(false);
        setIsLoadingConfig(false);
        return; // Don't fetch if we have config prop with data
      } else if (config === null) {
        // Config prop is explicitly null - parent says no config exists
        // Trust the parent's fetch result and don't refetch
        // Only clear fields if user hasn't edited them
        if (!userEditedRef.current.localAs) {
          setLocalAs('');
          setLocalAsError('');
        }
        if (!userEditedRef.current.routerId) {
          setRouterId('');
          setRouterIdError('');
        }
        // Clear routerId2 when config is null (for cluster mode) - only if user hasn't edited it
        if (!userEditedRef.current.routerId2 && onRouterId2Change) {
          onRouterId2Change('');
        }
        lastSyncedConfigRef.current = { localAs: null, routerId: null, routerId2: null };
        setHasNoInitialConfig(true);
        setIsLoadingConfig(false);
        return; // Don't fetch - parent already determined no config exists
      } else if (config && !hasLocalAs && !hasRouterId) {
        // Config is an object but has no localAs/routerId values
        // Check if they're undefined (not fetched yet) vs null (explicitly set to null)
        const localAsUndefined = config.localAs === undefined;
        const routerIdUndefined = config.routerId === undefined;
        
        if (localAsUndefined && routerIdUndefined) {
          // localAs and routerId are undefined - parent hasn't fetched yet, so we should fetch
          // Don't return here, continue to fetch logic below
        } else {
          // localAs and routerId are null or empty strings - parent already fetched
          // Trust the parent's fetch result
          // Only clear fields if user hasn't edited them
          if (!userEditedRef.current.localAs) {
            setLocalAs('');
            setLocalAsError('');
          }
          if (!userEditedRef.current.routerId) {
            setRouterId('');
            setRouterIdError('');
          }
          if (!userEditedRef.current.routerId2 && onRouterId2Change) {
            onRouterId2Change('');
          }
          lastSyncedConfigRef.current = { localAs: null, routerId: null, routerId2: null };
          setHasNoInitialConfig(true);
          setIsLoadingConfig(false);
          return; // Don't fetch - parent already fetched
        }
      }
    }

    // Skip if we've already fetched with the same key
    // Only fetch once per cluster/reflector, regardless of config changes
    if (hasFetchedRef.current) {
      // Ensure loading state is cleared if we're not fetching
      setIsLoadingConfig(false);
      return;
    }

    let isMounted = true;
    
    const fetchBgpConfig = async () => {
      if (!clusterId && !reflectorId) {
        return;
      }
      
      // Mark as fetching immediately to prevent duplicate calls
      hasFetchedRef.current = true;
      
      setIsLoadingConfig(true);
      try {
        const token = await getAccessTokenSilentlyRef.current();
        if (!tenantId) return;
        
        if (context === 'cluster' && clusterId) {
          try {
            const response = await getBgpConfig(token, Number(clusterId), tenantId);
            if (!isMounted) {
              return;
            }
            
            if (response.data) {
              const data = response.data;
              const localAsValue = data.localAs?.toString() || '';
              const routerIdValue = data.routerId1 || '';
              const routerId2Value = data.routerId2 || '';
              setFetchedBgpConfigBody(data as Record<string, unknown>);
              
              // Only update if user hasn't edited these fields
              if (!userEditedRef.current.localAs) {
                setLocalAs(localAsValue);
                setLocalAsError(validateNumberField(localAsValue));
              }
              if (!userEditedRef.current.routerId) {
                setRouterId(routerIdValue);
                setRouterIdError(validateIPAddress(routerIdValue));
              }
              if (!userEditedRef.current.routerId2 && onRouterId2Change) {
                onRouterId2Change(routerId2Value);
              }
              
              // Update last synced config
              lastSyncedConfigRef.current = {
                localAs: data.localAs,
                routerId: routerIdValue,
                routerId2: routerId2Value,
              };
              
              setHasNoInitialConfig(false);
            } else {
              // Initialize with empty strings if no config exists
              // Only update if user hasn't edited these fields
              if (!userEditedRef.current.localAs) {
                setLocalAs('');
                setLocalAsError('');
              }
              if (!userEditedRef.current.routerId) {
                setRouterId('');
                setRouterIdError('');
              }
              if (!userEditedRef.current.routerId2 && onRouterId2Change) {
                onRouterId2Change('');
              }
              
              lastSyncedConfigRef.current = { localAs: null, routerId: null, routerId2: null };
              setHasNoInitialConfig(true);
              setFetchedBgpConfigBody(null);
            }
          } catch (clusterErr) {
            // Handle 404 as "no config exists" - allow user to create one
            if (clusterErr instanceof AxiosError && clusterErr.response?.status === 404) {
              if (!isMounted) {
                return;
              }
              setFetchedBgpConfigBody(null);
              // Only clear if user hasn't edited these fields
              if (!userEditedRef.current.localAs) {
                setLocalAs('');
                setLocalAsError('');
              }
              if (!userEditedRef.current.routerId) {
                setRouterId('');
                setRouterIdError('');
              }
              if (!userEditedRef.current.routerId2 && onRouterId2Change) {
                onRouterId2Change('');
              }
              lastSyncedConfigRef.current = { localAs: null, routerId: null, routerId2: null };
              setHasNoInitialConfig(true);
            } else {
              // Re-throw other errors to be handled by outer catch
              throw clusterErr;
            }
          }
        } else if (reflectorId && context === 'reflector') {
          // Only fetch for standalone reflector mode - ONE request only
          try {
            const response = await getReflectorBgpConfig(token, Number(reflectorId), tenantId);
            if (!isMounted) {
              return;
            }
            
            if (response.data) {
              const data = response.data;
              const localAsValue = data.localAs?.toString() || '';
              const routerIdValue = data.routerId || '';
              setFetchedBgpConfigBody(data as Record<string, unknown>);
              
              // Only update if user hasn't edited these fields
              if (!userEditedRef.current.localAs) {
                setLocalAs(localAsValue);
                setLocalAsError(validateNumberField(localAsValue));
              }
              if (!userEditedRef.current.routerId) {
                setRouterId(routerIdValue);
                setRouterIdError(validateIPAddress(routerIdValue));
              }
              
              // Update last synced config
              lastSyncedConfigRef.current = {
                localAs: data.localAs,
                routerId: routerIdValue,
                routerId2: undefined, // Reflector mode doesn't have routerId2
              };
              
              setHasNoInitialConfig(false);
            } else {
              // Initialize with empty strings if no config exists
              // Only update if user hasn't edited these fields
              if (!userEditedRef.current.localAs) {
                setLocalAs('');
                setLocalAsError('');
              }
              if (!userEditedRef.current.routerId) {
                setRouterId('');
                setRouterIdError('');
              }
              lastSyncedConfigRef.current = { localAs: null, routerId: null, routerId2: undefined };
              setHasNoInitialConfig(true);
              setFetchedBgpConfigBody(null);
            }
          } catch (reflectorErr) {
            // Handle 404 as "no config exists" - allow user to create one
            if (reflectorErr instanceof AxiosError && reflectorErr.response?.status === 404) {
              if (!isMounted) {
                return;
              }
              setFetchedBgpConfigBody(null);
              // Only clear if user hasn't edited these fields
              if (!userEditedRef.current.localAs) {
                setLocalAs('');
                setLocalAsError('');
              }
              if (!userEditedRef.current.routerId) {
                setRouterId('');
                setRouterIdError('');
              }
              lastSyncedConfigRef.current = { localAs: null, routerId: null, routerId2: undefined };
              setHasNoInitialConfig(true);
            } else {
              // Re-throw other errors to be handled by outer catch
              throw reflectorErr;
            }
          }
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }
        // Only log and reset fetch flag for non-404 errors (404s are handled above as "no config exists")
        if (!(err instanceof AxiosError && err.response?.status === 404)) {
          console.error('[BgpDetailsPage] Error fetching BGP config:', err);
          // Reset fetch flag on error so we can retry
          hasFetchedRef.current = false;
          fetchKeyRef.current = '';
        }
        // Initialize with empty strings on error (including 404s that weren't caught above)
        // Only clear if user hasn't edited these fields
        if (!userEditedRef.current.localAs) {
          setLocalAs('');
          setLocalAsError('');
        }
        if (!userEditedRef.current.routerId) {
          setRouterId('');
          setRouterIdError('');
        }
        if (!userEditedRef.current.routerId2 && onRouterId2Change) {
          onRouterId2Change('');
        }
        lastSyncedConfigRef.current = { localAs: null, routerId: null, routerId2: null };
        setHasNoInitialConfig(true);
        setFetchedBgpConfigBody(null);
      } finally {
        if (isMounted) {
          setIsLoadingConfig(false);
        }
      }
    };

    fetchBgpConfig();

    return () => {
      isMounted = false;
    };
  }, [clusterId, reflectorId, context, config, parentLoading, tenantId]); // Added config, parentLoading, and tenantId to dependencies

  const neighborsFetchingRef = useRef(false);

  const fetchNeighbors = async () => {
    const id = context === 'reflector' ? reflectorId : clusterId;
    if (!id) return;
    if (neighborsFetchingRef.current) return;
    neighborsFetchingRef.current = true;

    setTableLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      if (!tenantId) {
        neighborsFetchingRef.current = false;
        return;
      }
      const response = context === 'reflector' 
        ? await getReflectorBgpNeighbors(token, Number(id), tenantId)
        : await getClusterBgpNeighbors(token, Number(id), tenantId);

      let mappedNeighbors = [];
      if (context === 'cluster') {
        // For cluster mode, the data is in response.data.data
        // Each neighbor has reflectors array with status for each reflector
        mappedNeighbors = (response.data.data || []).map((neighbor: any) => {
          // Extract status from reflectors array
          // Match reflectors by instanceId and determine primary/secondary by role from cluster instances
          const reflectors = neighbor.reflectors || [];
          
          // Find primary reflector: match by instanceId where role is 'primary' or 'PRIMARY'
          const primaryRoleInfo = clusterReflectorRoles.find((r: any) => 
            r.role && (r.role.toLowerCase() === 'primary')
          );
          const primaryReflector = primaryRoleInfo
            ? reflectors.find((r: any) => r.instanceId === primaryRoleInfo.instanceId)
            : (clusterReflectors[0] 
                ? reflectors.find((r: any) => r.instanceId === clusterReflectors[0])
                : reflectors[0]); // Fallback to first reflector if no role info available
          
          // Find secondary reflector: match by instanceId where role is 'secondary' or 'SECONDARY'
          const secondaryRoleInfo = clusterReflectorRoles.find((r: any) => 
            r.role && (r.role.toLowerCase() === 'secondary')
          );
          const secondaryReflector = secondaryRoleInfo
            ? reflectors.find((r: any) => r.instanceId === secondaryRoleInfo.instanceId)
            : (clusterReflectors[1] 
                ? reflectors.find((r: any) => r.instanceId === clusterReflectors[1])
                : reflectors[1]); // Fallback to second reflector if no role info available
          
          const mappedNeighbor = {
            ...neighbor,
            id: neighbor.neighborIds?.[0] || neighbor.id,
            interfaceAddress: neighbor.localIPsString || neighbor.localIPs?.join(', ') || '-',
            neighborAddress: neighbor.neighborAddress,
            description: neighbor.description ?? '',
            peerAs: neighbor.peerAs?.toString() || '',
            localAs: neighbor.localAs?.toString() || '',
            operatingMode: neighbor.operatingMode?.toUpperCase() || 'ENTERPRISE',
            password: neighbor.password || neighbor.md5Password || null,
            reflectors: reflectors.map((r: any) => ({
              instanceId: r.instanceId,
              neighborId: r.neighborId || r.bgpNeighborId,
              bgpNeighborId: r.neighborId || r.bgpNeighborId,
              status: r.status || r.bgpStatus,
              statusChangedAt: r.statusChangedAt,
            })),
            neighborIds: neighbor.neighborIds || [],
            primaryStatus: primaryReflector?.status || primaryReflector?.bgpStatus || '',
            primaryStatusChangedAt: primaryReflector?.statusChangedAt,
            secondaryStatus: secondaryReflector?.status || secondaryReflector?.bgpStatus || '',
            secondaryStatusChangedAt: secondaryReflector?.statusChangedAt,
            primaryReflectorOnline: primaryReflector?.reflectorOnline ?? null,
            secondaryReflectorOnline: secondaryReflector?.reflectorOnline ?? null,
            primaryReflectorLastUpdate: primaryReflector?.reflectorLastUpdate ?? null,
            secondaryReflectorLastUpdate: secondaryReflector?.reflectorLastUpdate ?? null,
            status: primaryReflector?.status || primaryReflector?.bgpStatus || '',
            statusChangedAt: primaryReflector?.statusChangedAt,
            BFD: neighbor.BFD || false,
            bfdInterval: neighbor.bfdInterval || null,
            bfdRequiredMinRxInterval: neighbor.bfdRequiredMinRxInterval ?? null,
            bfdMultiplier: neighbor.bfdMultiplier || null,
            bfdLocalState: primaryReflector?.bfdLocalState || null,
            bfdRemoteState: primaryReflector?.bfdRemoteState || null,
            bfdStatusChangedAt: primaryReflector?.bfdStatusChangedAt || null,
            bfdDaemonTxInterval: primaryReflector?.bfdDaemonTxInterval ?? null,
            bfdDaemonRxInterval: primaryReflector?.bfdDaemonRxInterval ?? null,
            bfdDaemonMultiplier: primaryReflector?.bfdDaemonMultiplier ?? null,
            bfdDaemonMultiHop: primaryReflector?.bfdDaemonMultiHop ?? null,
            bfdNegotiatedTxInterval: primaryReflector?.bfdNegotiatedTxInterval ?? null,
            bfdNegotiatedDetectionTime: primaryReflector?.bfdNegotiatedDetectionTime ?? null,
            bfdRemoteTxInterval: primaryReflector?.bfdRemoteTxInterval ?? null,
            bfdRemoteRxInterval: primaryReflector?.bfdRemoteRxInterval ?? null,
            bfdRemoteMultiplier: primaryReflector?.bfdRemoteMultiplier ?? null,
            bfdRemoteDiscriminator: primaryReflector?.bfdRemoteDiscriminator ?? null,
            secondaryBfdLocalState: secondaryReflector?.bfdLocalState || null,
            secondaryBfdRemoteState: secondaryReflector?.bfdRemoteState || null,
            secondaryBfdStatusChangedAt: secondaryReflector?.bfdStatusChangedAt || null,
            secondaryBfdDaemonTxInterval: secondaryReflector?.bfdDaemonTxInterval ?? null,
            secondaryBfdDaemonRxInterval: secondaryReflector?.bfdDaemonRxInterval ?? null,
            secondaryBfdDaemonMultiplier: secondaryReflector?.bfdDaemonMultiplier ?? null,
            secondaryBfdDaemonMultiHop: secondaryReflector?.bfdDaemonMultiHop ?? null,
            secondaryBfdNegotiatedTxInterval: secondaryReflector?.bfdNegotiatedTxInterval ?? null,
            secondaryBfdNegotiatedDetectionTime: secondaryReflector?.bfdNegotiatedDetectionTime ?? null,
            secondaryBfdRemoteTxInterval: secondaryReflector?.bfdRemoteTxInterval ?? null,
            secondaryBfdRemoteRxInterval: secondaryReflector?.bfdRemoteRxInterval ?? null,
            secondaryBfdRemoteMultiplier: secondaryReflector?.bfdRemoteMultiplier ?? null,
            secondaryBfdRemoteDiscriminator: secondaryReflector?.bfdRemoteDiscriminator ?? null,
            prefixesReceived: primaryReflector?.prefixesReceived ?? null,
            prefixesAccepted: primaryReflector?.prefixesAccepted ?? null,
            prefixesAdvertised: primaryReflector?.prefixesAdvertised ?? null,
            secondaryPrefixesReceived: secondaryReflector?.prefixesReceived ?? null,
            secondaryPrefixesAccepted: secondaryReflector?.prefixesAccepted ?? null,
            secondaryPrefixesAdvertised: secondaryReflector?.prefixesAdvertised ?? null,
          };
          return mappedNeighbor;
        });
      } else {
        mappedNeighbors = (response.data || []).map((neighbor: any) => ({
          ...neighbor,
          id: neighbor.id,
          description: neighbor.description ?? '',
          interfaceAddress:
            typeof neighbor.interfaceAddress === 'object' && neighbor.interfaceAddress !== null
              ? neighbor.interfaceAddress.ipAddress
              : neighbor.interfaceAddress || '-',
          statusChangedAt: neighbor.statusChangedAt,
          password: neighbor.password || neighbor.md5Password || null,
          prefixesReceived: neighbor.prefixesReceived ?? null,
          prefixesAccepted: neighbor.prefixesAccepted ?? null,
          prefixesAdvertised: neighbor.prefixesAdvertised ?? null,
        }));
      }

      setNeighbors(mappedNeighbors);
    } catch (err) {
      // Don't set error if we have no initial config - allow user to create one
      if (!hasNoInitialConfig) {
        setError('Failed to load BGP neighbors');
      }
      console.error('Error fetching BGP neighbors:', err);
    } finally {
      setTableLoading(false);
      neighborsFetchingRef.current = false;
    }
  };

  const neighborsFetchKeyRef = useRef('');

  useEffect(() => {
    if (parentLoading) return;
    const id = context === 'reflector' ? reflectorId : clusterId;
    if (!id || !tenantId) return;

    const key = `${id}-${context}-${tenantId}`;
    if (neighborsFetchKeyRef.current === key) return;
    neighborsFetchKeyRef.current = key;

    fetchNeighbors();
  }, [reflectorId, clusterId, context, tenantId, parentLoading]);

  const handleDeleteBgpNeighbor = async (neighborId: number) => {
    // Close confirmation dialog first
    setDeleteConfirmState({
      isOpen: false,
      neighborId: null,
      neighborAddress: '',
    });

    setIsDeleting(true);
    try {
      const token = await getAccessTokenSilently();
      if (!tenantId) return;
      await deleteReflectorBgpNeighbor(token, neighborId, tenantId);
      
      // Show success alert first
      setAlertState({
        isOpen: true,
        title: "Success",
        description: "BGP neighbor has been deleted successfully.",
        variant: "success"
      });

      // Wait for alert to be shown before refreshing
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Then refresh the table
      await fetchNeighbors();
    } catch (error) {
      console.error('Error deleting BGP neighbor:', error);
      // Show error alert
      setAlertState({
        isOpen: true,
        title: "Error",
        description: "Failed to delete BGP neighbor. Please try again.",
        variant: "error"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteClick = (neighborId: number, neighborAddress: string) => {
    setDeleteConfirmState({
      isOpen: true,
      neighborId,
      neighborAddress,
    });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirmState.neighborId !== null) {
      handleDeleteBgpNeighbor(deleteConfirmState.neighborId);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return; // Prevent canceling during deletion
    setDeleteConfirmState({
      isOpen: false,
      neighborId: null,
      neighborAddress: '',
    });
  };

  const renderBgpStatusTag = (
    status: string | undefined,
    statusChangedAt?: number | Date,
    reflectorOnline?: boolean | null,
    reflectorLastUpdate?: string | number | Date | null,
  ) => {
    // The reflector that reports this session stopped sending keepalives: its
    // persisted status is frozen, so render "no data" instead of a live state.
    if (reflectorOnline === false) {
      return (
        <GenericTag
          label={buildReflectorDownLabel(reflectorLastUpdate)}
          variant="default"
          tooltip={hasStatus(status)
            ? `Last known state: ${buildStatusLabel(status, statusChangedAt)}`
            : undefined}
        />
      );
    }
    if (!hasStatus(status)) return <GenericTag label="-" variant="default" />;
    return <GenericTag label={buildStatusLabel(status, statusChangedAt)} variant={getStatusVariant(status)} />;
  };

  // A row corresponds to a cluster neighbor whenever any of the primary/secondary
  // cluster-only fields are present. The Primary/Secondary role labels are
  // rendered once per row in a dedicated leading column (see roleColumn below),
  // so cluster cells here render two aligned lines without repeating the label.
  const isClusterRow = (row: any) => (
    row.primaryStatus !== undefined
    || row.secondaryStatus !== undefined
    || row.secondaryPrefixesReceived !== undefined
    || row.secondaryPrefixesAdvertised !== undefined
    || row.secondaryBfdLocalState !== undefined
    || row.secondaryBfdRemoteState !== undefined
    || row.secondaryBfdDaemonRxInterval !== undefined
    || row.secondaryBfdDaemonMultiplier !== undefined
  );

  const renderBgpStatus = (row: any) => {
    if (!isClusterRow(row)) {
      return renderBgpStatusTag(row.status, row.statusChangedAt);
    }
    return (
      <div className="flex flex-col gap-1">
        {renderBgpStatusTag(row.primaryStatus, row.primaryStatusChangedAt, row.primaryReflectorOnline, row.primaryReflectorLastUpdate)}
        {renderBgpStatusTag(row.secondaryStatus, row.secondaryStatusChangedAt, row.secondaryReflectorOnline, row.secondaryReflectorLastUpdate)}
      </div>
    );
  };

  const renderPrefixLine = (rcvd: any, adv: any) => {
    if (rcvd == null && adv == null) return null;
    return <span className="font-mono text-xs whitespace-nowrap">{rcvd ?? 0} / {adv ?? 0}</span>;
  };

  const renderPrefixes = (row: any) => {
    // Frozen counters from a reflector that stopped reporting are hidden.
    const primaryLine = row.primaryReflectorOnline === false
      ? null
      : renderPrefixLine(row.prefixesReceived, row.prefixesAdvertised);
    if (!isClusterRow(row)) {
      return primaryLine || <span className="text-semantic-text-secondary">-</span>;
    }
    const secondaryLine = row.secondaryReflectorOnline === false
      ? null
      : renderPrefixLine(row.secondaryPrefixesReceived, row.secondaryPrefixesAdvertised);
    if (!primaryLine && !secondaryLine) return <span className="text-semantic-text-secondary">-</span>;
    return (
      <div className="flex flex-col gap-1">
        {primaryLine || <span className="text-semantic-text-secondary text-xs">-</span>}
        {secondaryLine || <span className="text-semantic-text-secondary text-xs">-</span>}
      </div>
    );
  };

  const renderBfdStatus = (row: any) => {
    if (!row.BFD) {
      return <GenericTag label="Disable" variant="default" size="small" />;
    }

    const primaryLabel = buildBfdLabel(row.BFD, row.bfdLocalState, row.bfdRemoteState, row.bfdStatusChangedAt);
    if (!isClusterRow(row)) {
      if (!primaryLabel) return <span className="text-semantic-text-secondary">-</span>;
      return <GenericTag label={primaryLabel} variant={getBfdVariant(row.BFD, row.bfdLocalState, row.bfdRemoteState)} size="small" />;
    }
    const secondaryLabel = buildBfdLabel(row.BFD, row.secondaryBfdLocalState, row.secondaryBfdRemoteState, row.secondaryBfdStatusChangedAt);
    if (!primaryLabel && !secondaryLabel) return <span className="text-semantic-text-secondary">-</span>;
    const renderBfdSide = (label: string | null, variant: 'success' | 'error' | 'warning' | 'default', online?: boolean | null) => {
      // Reflector not reporting: its persisted BFD state is frozen, hide it.
      if (online === false) {
        return <span className="text-semantic-text-secondary text-xs inline-flex items-center h-6">-</span>;
      }
      if (!label) return <span className="text-semantic-text-secondary text-xs">-</span>;
      return <GenericTag label={label} variant={variant} size="small" />;
    };
    return (
      <div className="flex flex-col gap-1">
        {renderBfdSide(primaryLabel, getBfdVariant(row.BFD, row.bfdLocalState, row.bfdRemoteState), row.primaryReflectorOnline)}
        {renderBfdSide(secondaryLabel, getBfdVariant(row.BFD, row.secondaryBfdLocalState, row.secondaryBfdRemoteState), row.secondaryReflectorOnline)}
      </div>
    );
  };

  // Cluster-wide reflector health, derived from the per-neighbor liveness
  // flags the grouped endpoint reports. A side counts as down only when every
  // row that knows about it says it is down (avoids flagging on partial data).
  const reflectorHealth = useMemo(() => {
    if (context !== 'cluster' || neighbors.length === 0) return null;
    const rows = neighbors as any[];
    const sideDown = (side: 'primary' | 'secondary') => {
      const values = rows
        .map(r => (side === 'primary' ? r.primaryReflectorOnline : r.secondaryReflectorOnline))
        .filter((v: any) => v === true || v === false);
      return values.length > 0 && values.every((v: boolean) => v === false);
    };
    const lastSeen = (side: 'primary' | 'secondary') =>
      rows
        .map(r => (side === 'primary' ? r.primaryReflectorLastUpdate : r.secondaryReflectorLastUpdate))
        .find(Boolean) ?? null;
    return {
      primaryDown: sideDown('primary'),
      secondaryDown: sideDown('secondary'),
      primaryLastSeen: lastSeen('primary'),
      secondaryLastSeen: lastSeen('secondary'),
    };
  }, [context, neighbors]);

  // Compact header chip: one short pill next to the section title, with the
  // full explanation in the hover tooltip. Deliberately quiet — the per-row
  // "No data" tags already carry the detail.
  const renderReflectorHealthChip = () => {
    if (!reflectorHealth) return null;
    const { primaryDown, secondaryDown, primaryLastSeen, secondaryLastSeen } = reflectorHealth;
    if (!primaryDown && !secondaryDown) return null;

    const downFor = (lastSeen: any) =>
      lastSeen ? ` · ${formatCiscoUptime(new Date(lastSeen))}` : '';

    let variant: 'error' | 'warning' = 'error';
    let label = `Primary reflector offline${downFor(primaryLastSeen)}`;
    let tooltip = 'Secondary is serving. Primary session data is last known state — failover assumed from missed keepalives.';

    if (primaryDown && secondaryDown) {
      label = `Reflector cluster offline${downFor(primaryLastSeen || secondaryLastSeen)}`;
      tooltip = 'No data being received from either reflector. Showing last known state.';
    } else if (secondaryDown) {
      variant = 'warning';
      label = `Secondary reflector offline${downFor(secondaryLastSeen)}`;
      tooltip = 'HA redundancy lost. Traffic unaffected — primary is serving.';
    }

    return <GenericTag label={label} variant={variant} size="small" rounded="full" tooltip={tooltip} />;
  };

  // Compact single-line BFD cell: we only render the announced "RX / xMult"
  // here; negotiated/remote/state details are surfaced through the hover
  // tooltip (see renderBfdTooltipContent) to keep the table tidy.
  // We force h-6 so each line matches the GenericTag height used in the
  // BFD Status column (size="small" => h-6). That keeps primary/secondary
  // rows vertically aligned with their corresponding status tags.
  const renderBfdConfigLine = (
    daemonRx: any,
    daemonMult: any,
    _negotiatedTx?: any,
    _detectionTime?: any
  ) => {
    if (daemonRx == null && daemonMult == null) return null;
    const announcedLine = `${daemonRx ?? '-'}ms / x${daemonMult ?? '-'}`;
    return (
      <span className="font-mono text-xs whitespace-nowrap inline-flex items-center h-6">
        {announcedLine}
      </span>
    );
  };

  // Picks the correct BFD telemetry fields for either the primary or the
  // secondary reflector in a cluster row. All primary/secondary telemetry
  // keys share the same shape; we centralize the mapping here so the
  // tooltip/details view below doesn't have to branch on `side` everywhere.
  const pickBfdSideData = (row: any, side: 'primary' | 'secondary') => {
    const sec = side === 'secondary';
    return {
      localState: sec ? row.secondaryBfdLocalState : row.bfdLocalState,
      remoteState: sec ? row.secondaryBfdRemoteState : row.bfdRemoteState,
      statusChangedAt: sec ? row.secondaryBfdStatusChangedAt : row.bfdStatusChangedAt,
      daemonTx: sec ? row.secondaryBfdDaemonTxInterval : row.bfdDaemonTxInterval,
      daemonRx: sec ? row.secondaryBfdDaemonRxInterval : row.bfdDaemonRxInterval,
      daemonMult: sec ? row.secondaryBfdDaemonMultiplier : row.bfdDaemonMultiplier,
      daemonMultiHop: sec ? row.secondaryBfdDaemonMultiHop : row.bfdDaemonMultiHop,
      negotiatedTx: sec ? row.secondaryBfdNegotiatedTxInterval : row.bfdNegotiatedTxInterval,
      negotiatedDetectionTime: sec ? row.secondaryBfdNegotiatedDetectionTime : row.bfdNegotiatedDetectionTime,
      remoteTx: sec ? row.secondaryBfdRemoteTxInterval : row.bfdRemoteTxInterval,
      remoteRx: sec ? row.secondaryBfdRemoteRxInterval : row.bfdRemoteRxInterval,
      remoteMult: sec ? row.secondaryBfdRemoteMultiplier : row.bfdRemoteMultiplier,
      remoteDisc: sec ? row.secondaryBfdRemoteDiscriminator : row.bfdRemoteDiscriminator,
    };
  };

  const fmtMs = (v: any) => (v == null ? '—' : `${v} ms`);
  const fmtNum = (v: any) => (v == null ? '—' : String(v));
  const fmtBool = (v: any) => (v == null ? '—' : v ? 'yes' : 'no');

  // Structured BFD tooltip content. Grouped by what the value represents
  // (config vs. on-wire local/remote vs. derived negotiated) so you can
  // spot mismatches at a glance (e.g. configured RX != negotiated RX means
  // the peer's TX dominates the detection time).
  const renderBfdTooltipContent = (row: any, side: 'primary' | 'secondary') => {
    const d = pickBfdSideData(row, side);
    const configuredRx = row.bfdRequiredMinRxInterval ?? row.bfdInterval;
    const rolePill = isClusterRow(row) ? (side === 'primary' ? 'Primary' : 'Secondary') : null;

    const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
      <div className="flex justify-between gap-4">
        <span className="text-semantic-text-secondary">{label}</span>
        <span className="font-mono text-semantic-text-primary">{value}</span>
      </div>
    );
    const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
      <div className="flex flex-col gap-0.5">
        <span className="text-[10px] uppercase tracking-wide text-semantic-text-secondary">{title}</span>
        {children}
      </div>
    );

    return (
      <div className="flex flex-col gap-2 text-xs min-w-[240px]">
        {rolePill && (
          <div className="flex items-center justify-between">
            <span className="font-semibold text-semantic-text-primary">BFD — {rolePill}</span>
            <span className="text-[10px] text-semantic-text-secondary">{row.neighborAddress}</span>
          </div>
        )}

        <Section title="Configured (user)">
          <Row label="Desired Min TX" value={fmtMs(row.bfdInterval)} />
          <Row label="Required Min RX" value={fmtMs(configuredRx)} />
          <Row label="Detect Mult" value={fmtNum(row.bfdMultiplier)} />
        </Section>

        <Section title="Local (announced by us)">
          <Row label="TX" value={fmtMs(d.daemonTx)} />
          <Row label="RX" value={fmtMs(d.daemonRx)} />
          <Row label="Mult" value={fmtNum(d.daemonMult)} />
          <Row label="Multi-hop" value={fmtBool(d.daemonMultiHop)} />
        </Section>

        <Section title="Remote (announced by peer)">
          <Row label="TX" value={fmtMs(d.remoteTx)} />
          <Row label="RX" value={fmtMs(d.remoteRx)} />
          <Row label="Mult" value={fmtNum(d.remoteMult)} />
          <Row label="Discriminator" value={fmtNum(d.remoteDisc)} />
        </Section>

        <Section title="Negotiated (effective)">
          <Row label="Send interval" value={fmtMs(d.negotiatedTx)} />
          <Row label="Detection time" value={fmtMs(d.negotiatedDetectionTime)} />
        </Section>

        <Section title="Session">
          <Row label="Local state" value={fmtNum(d.localState)} />
          <Row label="Remote state" value={fmtNum(d.remoteState)} />
          <Row
            label="Last change"
            value={d.statusChangedAt ? new Date(Number(d.statusChangedAt) * 1000).toLocaleString() : '—'}
          />
        </Section>
      </div>
    );
  };

  // Wraps a cell with the rich BFD tooltip. Only attaches the tooltip when
  // there is actually something useful to show, otherwise returns the cell
  // as-is to avoid dangling hover zones on empty data.
  const withBfdTooltip = (
    row: any,
    side: 'primary' | 'secondary',
    cell: React.ReactNode
  ) => {
    if (!row.BFD) return cell;
    const d = pickBfdSideData(row, side);
    const hasAny =
      d.daemonTx != null || d.daemonRx != null || d.daemonMult != null ||
      d.remoteTx != null || d.remoteRx != null || d.remoteMult != null ||
      d.negotiatedTx != null || d.negotiatedDetectionTime != null ||
      d.localState != null || d.remoteState != null;
    if (!hasAny) return cell;
    return (
      <GenericTooltip
        text="bfd-details"
        position="left"
        width="22rem"
        horizontalAlign="right"
        content={renderBfdTooltipContent(row, side)}
      >
        <span className="inline-block cursor-help">{cell}</span>
      </GenericTooltip>
    );
  };

  const renderBfdConfig = (row: any) => {
    if (!row.BFD) {
      return <span className="text-semantic-text-secondary">-</span>;
    }
    const primaryLine = renderBfdConfigLine(
      row.bfdDaemonRxInterval,
      row.bfdDaemonMultiplier,
      row.bfdNegotiatedTxInterval,
      row.bfdNegotiatedDetectionTime
    );
    if (!isClusterRow(row)) {
      if (!primaryLine) return <span className="text-semantic-text-secondary">-</span>;
      return withBfdTooltip(row, 'primary', primaryLine);
    }
    const secondaryLine = renderBfdConfigLine(
      row.secondaryBfdDaemonRxInterval,
      row.secondaryBfdDaemonMultiplier,
      row.secondaryBfdNegotiatedTxInterval,
      row.secondaryBfdNegotiatedDetectionTime
    );
    if (!primaryLine && !secondaryLine) return <span className="text-semantic-text-secondary">-</span>;
    const placeholder = (
      <span className="text-semantic-text-secondary text-xs inline-flex items-center h-6">-</span>
    );
    const primaryCell = primaryLine
      ? withBfdTooltip(row, 'primary', primaryLine)
      : placeholder;
    const secondaryCell = secondaryLine
      ? withBfdTooltip(row, 'secondary', secondaryLine)
      : placeholder;
    return (
      <div className="flex flex-col gap-1">
        {primaryCell}
        {secondaryCell}
      </div>
    );
  };

  // Use passed columns or default, but override Status column for cluster mode and Actions column:
  const baseColumns = defaultBgpNeighborsColumns.filter(col => col.header !== 'Actions' && col.header !== 'Status');
  
  // Cluster-only leading column that shows the Primary/Secondary role labels
  // once per row. For standalone neighbors it collapses to an empty cell so
  // the surrounding layout stays identical.
  const roleColumn: GenericTableColumn<BgpNeighbor> = {
    header: '',
    accessor: undefined,
    renderCell: (row: any) => {
      if (!isClusterRow(row)) return null;
      return (
        <div className="flex flex-col gap-1 text-[10px] text-semantic-text-secondary leading-[22px]">
          <span>Primary</span>
          <span>Secondary</span>
        </div>
      );
    },
  };

  const statusColumns: GenericTableColumn<BgpNeighbor>[] = [
    {
      header: 'Status',
      accessor: 'status',
      renderCell: (row: any) => renderBgpStatus(row),
    },
  ];

  const prefixColumn: GenericTableColumn<BgpNeighbor> = {
    header: 'Rcvd / Adv',
    accessor: 'prefixesReceived' as keyof BgpNeighbor,
    renderCell: (row: any) => renderPrefixes(row),
  };

  const bfdStatusColumn: GenericTableColumn<BgpNeighbor> = {
    header: 'BFD Status',
    accessor: 'bfdLocalState' as keyof BgpNeighbor,
    renderCell: (row: any) => renderBfdStatus(row),
  };

  const bfdConfigColumn: GenericTableColumn<BgpNeighbor> = {
    header: 'BFD Intv / Mult',
    accessor: 'bfdInterval' as keyof BgpNeighbor,
    renderCell: (row: any) => renderBfdConfig(row),
  };

  const tableColumns: GenericTableColumn<BgpNeighbor>[] = [
    ...baseColumns,
    roleColumn,
    ...statusColumns,
    prefixColumn,
    bfdStatusColumn,
    bfdConfigColumn,
    {
      header: 'Actions',
      accessor: undefined,
      renderCell: (row: any) => (
        <div className="flex items-center gap-2">
          <GenericButton
            isIconOnly
            icon={<IconEdit className="w-8 h-8" />}
            variant="secondary"
            shape="filled"
            size="sm"
            aria-label="Edit"
            onClick={() => {
              setEditMode(true);
              // Pass the full row data including reflectors array for individual bgpNeighborIds
              // Modal will still fetch details via API for complete data, but needs reflectors for IDs
              setEditRow(row);
              setIsAddModalOpen(true);
            }}
          />
          <GenericButton
            isIconOnly
            icon={<IconDelete className="w-6 h-6" />}
            variant="danger"
            shape="filled"
            size="sm"
            aria-label="Delete"
            onClick={() => {
              handleDeleteClick(row.id, row.neighborAddress || '');
            }}
            disabled={isDeleting || deleteConfirmState.isOpen}
            loading={isDeleting && deleteConfirmState.neighborId === row.id}
          />
        </div>
      ),
    },
  ];

  const handleEditBgpNeighbor = async (updatedData: any) => {
    setIsAddModalOpen(false);
    setEditRow(null);
    setEditMode(false);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    fetchNeighbors();
  };

  const handleAddBgpNeighbor = async (neighbor: any) => {
    setIsAddModalOpen(false);
    setEditRow(null);
    setEditMode(false);
    
    await new Promise(resolve => setTimeout(resolve, 1000));
    fetchNeighbors();
  };

  const handleSaveConfiguration = async () => {
    // Check for validation errors before submitting
    if (localAsError || routerIdError) {
      return; // Don't submit if there are validation errors
    }

    setIsSaving(true);
    try {
      const token = await getAccessTokenSilently();
      
      if (context === 'cluster' && clusterId) {
        // Cluster mode: use cluster-specific endpoints
        if (!tenantId) return;
        const payload = {
          localAs: Number(localAs),
          routerId1: routerId,
          routerId2: routerId2
        };

        await updateBgpConfig(token, Number(clusterId), payload, tenantId);
      } else if (reflectorId && context === 'reflector') {
        // Reflector mode: use reflector-specific endpoint
        if (!tenantId) return;
        const payload: ReflectorBgpConfig = {
          localAs: Number(localAs),
          routerId: routerId
        };

        await updateReflectorBgpConfig(token, Number(reflectorId), payload, tenantId);
      }
      
      // Update state to reflect that config now exists
      setHasNoInitialConfig(false);
      
      // Show success alert
      setAlertState({
        isOpen: true,
        title: "Success",
        description: "BGP configuration has been updated successfully.",
        variant: "success"
      });

      // Refresh the neighbors list
      await fetchNeighbors();
    } catch (error) {
      console.error('Error updating BGP configuration:', error);
      // Show error alert
      setAlertState({
        isOpen: true,
        title: "Error",
        description: "Failed to update BGP configuration. Please try again.",
        variant: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  };

  const handleRouterIdClick = (reflectorId: string) => {
    if (reflectorId) {
      navigate(`/reflector-v2/management/bgp-reflectors/details/${reflectorId}`);
    }
  };

  // Memoized boolean to determine if Save Configuration button should be disabled
  const isSaveButtonDisabled = useMemo(() => {
    const hasValidLocalAs = String(localAs || '').trim().length > 0;
    const hasValidRouterId = String(routerId || '').trim().length > 0;
    const hasValidationErrors = !!localAsError || !!routerIdError;
    
    // In cluster mode (showReflector2), routerId2 is also required
    const hasValidRouterId2 = showReflector2 
      ? String(routerId2 || '').trim().length > 0 
      : true; // Not required in standalone mode
    
    return !hasValidLocalAs || !hasValidRouterId || !hasValidRouterId2 || isSaving || hasValidationErrors || isLoadingConfig;
  }, [localAs, routerId, routerId2, showReflector2, isSaving, localAsError, routerIdError, isLoadingConfig]);

  // Only show EmptyItems if there's a real error, not when there's no initial config
  if (error && !hasNoInitialConfig) {
    return <EmptyItems />;
  }

  return (
    <div className="relative">
      <GenericCard isCollapsible={false} className="flex flex-col">
        <div className="flex flex-col gap-8 p-6">
          {/* First load: skeleton only; saving: show "Saving..." with skeleton */}
          {isFirstLoad && (
            <>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <SkeletonLine w={240} h={28} className="rounded" />
                  <SkeletonLine w={160} h={40} className="rounded" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <SkeletonLine w="30%" h={14} className="rounded mb-2" />
                      <SkeletonLine w="100%" h={40} className="rounded" />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <SkeletonLine w={200} h={28} className="rounded" />
                  <SkeletonLine w={140} h={40} className="rounded" />
                </div>
                <TableSkeleton columns={8} rows={5} />
              </div>
            </>
          )}
          {isSaving && (
            <div className="flex items-center gap-2 mb-4" role="status" aria-live="polite">
              <SkeletonLine w={24} h={24} className="rounded shrink-0" />
              <SkeletonLine w={180} h={20} className="rounded" />
            </div>
          )}
          {!isFirstLoad && (
            <>
          {/* Global/Cluster BGP Configuration */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Global BGP Configuration</h2>
              <GenericButton 
                label="Save Configuration" 
                variant="brand" 
                size="md" 
                onClick={handleSaveConfiguration}
                disabled={isSaveButtonDisabled}
              />
            </div>
            {showReflector2 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <GenericInput
                    label="Local AS"
                    value={localAs}
                    onChange={handleLocalAsChange}
                    type="text"
                    className="mb-1"
                    hasError={!!localAsError}
                    helperText={localAsError || "Local Autonomous System number"}
                  />
                </div>
                <div>
                  <GenericInput
                    label={
                      <span>
                        Router ID (
                        <button
                          onClick={() => handleRouterIdClick(clusterReflectors[0]?.toString() || '')}
                          className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
                          disabled={!clusterReflectors[0]}
                        >
                          {clusterReflectorNames[0] || 'Reflector-1'}
                        </button>
                        )
                      </span>
                    }
                    value={routerId}
                    onChange={handleRouterIdChange}
                    type="text"
                    className="mb-1"
                    hasError={!!routerIdError}
                    helperText={routerIdError || "BGP Router ID for Reflector-1"}
                  />
                </div>
                <div>
                  <GenericInput
                    label={
                      <span>
                        Router ID (
                        <button
                          onClick={() => handleRouterIdClick(clusterReflectors[1]?.toString() || '')}
                          className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
                          disabled={!clusterReflectors[1]}
                        >
                          {clusterReflectorNames[1] || 'Reflector-2'}
                        </button>
                        )
                      </span>
                    }
                    value={routerId2}
                    onChange={handleRouterId2Change}
                    type="text"
                    className="mb-1"
                  />
                  <div className="text-xs text-gray-500 mt-1">BGP Router ID for Reflector-2</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <GenericInput
                    label="Local AS"
                    value={localAs}
                    onChange={handleLocalAsChange}
                    type="text"
                    className="mb-1"
                    hasError={!!localAsError}
                    helperText={localAsError || "Local Autonomous System number"}
                  />
                </div>
                <div>
                  <GenericInput
                    label={
                      <span>
                        Router ID (
                        <button
                          onClick={() => handleRouterIdClick(reflectorId || '')}
                          className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
                          disabled={!reflectorId}
                        >
                          {reflectorName}
                        </button>
                        )
                      </span>
                    }
                    value={routerId}
                    onChange={handleRouterIdChange}
                    type="text"
                    className="mb-1"
                    hasError={!!routerIdError}
                    helperText={routerIdError || "BGP Router ID (must be a valid IPv4 address)"}
                  />
                </div>
              </div>
            )}
          </div>

          {/* BGP Neighbors */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">BGP Neighbors</h2>
                <button
                  onClick={() => fetchNeighbors()}
                  disabled={tableLoading}
                  className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  title="Refresh"
                >
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20.49 9A9 9 0 0 0 5.64 5.64L4 9m16 6l-1.64 3.36A9 9 0 0 1 3.51 15" />
                  </svg>
                </button>
                {!tableLoading && renderReflectorHealthChip()}
              </div>
              <div className="flex flex-col items-end gap-1">
              <GenericButton
                label="+ Add BGP Neighbor"
                variant="brand"
                size="md"
                onClick={() => {
                  setEditMode(false);
                  setEditRow(null);
                  setIsAddModalOpen(true);
                }}
                disabled={
                  hasNoInitialConfig ||
                  isLoadingConfig ||
                  !localAs ||
                  !routerId ||
                  isAtBgpNeighborLimit
                }
                title={
                  addBgpNeighborDisabledReason ||
                  (hasNoInitialConfig || !localAs || !routerId
                    ? "Save global BGP configuration before adding neighbors."
                    : undefined)
                }
              />
              {isAtBgpNeighborLimit && (
                <span className="text-xs text-semantic-text-secondary text-right max-w-xs">
                  {addBgpNeighborDisabledReason}
                </span>
              )}
              </div>
            </div>
            {!isFirstLoad && (
              <DataStateView
                loading={tableLoading}
                error={null}
                isEmpty={neighbors.length === 0}
                onRetry={fetchNeighbors}
                loadingContent={<TableSkeleton columns={8} rows={5} />}
                errorTitle="Failed to load BGP neighbors"
                emptyTitle="You have no BGP neighbors yet"
                emptyDescription="Add a BGP neighbor to get started."
              >
                <GenericTable
                  columns={tableColumns}
                  data={neighbors}
                  initialRowsPerPage={getInitialRowsPerPage(neighbors.length)}
                  rowsPerPageOptions={[...REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS]}
                  width="100%"
                  maxTableBodyHeight={getTableBodyMaxHeight(BGP_NEIGHBORS_CHROME_PX)}
                />
              </DataStateView>
            )}
          </div>
            </>
          )}
        </div>
      </GenericCard>
      {/* Add/Edit Modal */}
      <BgpModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={editMode ? handleEditBgpNeighbor : handleAddBgpNeighbor}
        mode={editMode ? "edit" : "add"}
        initialValues={editRow || {}}
        reflectorId={reflectorId ? Number(reflectorId) : undefined}
        getAccessTokenSilently={getAccessTokenSilently}
        context={context}
        clusterId={clusterId ? Number(clusterId) : undefined}
        clusterReflectors={clusterReflectors}
        clusterInterfaceIds={clusterInterfaceIds}
      />

      {/* Delete confirmation alert */}
      <GenericAlert
        isOpen={deleteConfirmState.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete BGP Neighbor"
        description={`Are you sure you want to delete the BGP neighbor ${deleteConfirmState.neighborAddress}? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="error"
        confirmButtonDisabled={isDeleting}
      />

      {/* Alert for configuration updates */}
      <GenericAlert
        isOpen={alertState.isOpen}
        onClose={handleCloseAlert}
        onConfirm={handleCloseAlert}
        title={alertState.title}
        description={alertState.description}
        confirmLabel="OK"
        variant={alertState.variant}
      />
    </div>
  );
};

export default BgpDetailsPage;
